import { useSubscriptionDialogs } from '../hooks/useSubscriptionDialogs';
import SummaryStrip from '../components/SummaryStrip';
import { PageHeader, StatCard as MetricCard, LoadingState } from '../components/AdminPageUI';
import { useEffect, useState, useContext, useCallback, useMemo } from 'react';
import { Box, Typography, Paper, Grid, IconButton, Badge, Menu, MenuItem, Divider, Stack, Tooltip, Dialog, DialogTitle, DialogContent, Alert, TextField, Button, FormControl, InputLabel, Select, CircularProgress, DialogActions, Chip, useMediaQuery, alpha } from '@mui/material';
import {
  People, School, Class, Assignment,
  FamilyRestroom,
  Event, Refresh, NotificationImportant, CheckCircle, Brightness7
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { useNavigate } from 'react-router-dom';
import { Bar, Doughnut, Line, PolarArea, Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  ArcElement, Tooltip as ChartTooltip, Legend, PointElement, LineElement, RadialLinearScale, Filler
} from 'chart.js';
import api from '../utils/Api';
import socket from '../utils/socket';
import { motion } from 'framer-motion';
import { ColorModeContext } from '../App';
import { useTheme } from '@mui/material/styles';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { format, parseISO, isSameDay } from 'date-fns';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { PickersDay } from '@mui/x-date-pickers';
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// Register chart components
ChartJS.register(
  CategoryScale, LinearScale, BarElement,
  ArcElement, ChartTooltip, Legend, PointElement, LineElement,
  RadialLinearScale, Filler
);

const StatCard = ({ title, count, icon, subtitle, themeColor = 'primary', isWeekTracker, weekData }) => (
  <MetricCard title={title} icon={icon} color={themeColor}
    value={isWeekTracker ? (weekData ? `${weekData.current}/${weekData.total}` : undefined) : count}
    description={isWeekTracker && weekData ? `${weekData.termName} • ${weekData.academicYear}` : subtitle}
    loading={isWeekTracker && !weekData}
  />
);

const eventColors = {
  student: '#2196F3',
  teacher: '#FF9800',
  parent: '#9C27B0',
  class: '#4CAF50',
  all: '#9E9E9E',
};

const formatWeekLabel = (week) => {
  const raw = String(week ?? '').trim();
  if (!raw) return '';

  const weekNumber = raw.match(/\d+/)?.[0];
  if (weekNumber) return `Week ${Number(weekNumber)}`;

  return raw;
};

const ATTENDANCE_DAY_KEYS = ['M', 'T', 'W', 'TH', 'F'];

const getWeekNumber = (week) => Number(formatWeekLabel(week).match(/\d+/)?.[0] || 0);

const normalizeWeeklyAttendance = (weeklyAttendance = []) => {
  const weekMap = new Map();

  weeklyAttendance.forEach((weekItem) => {
    const weekLabel = formatWeekLabel(weekItem?.week);
    if (!weekLabel) return;

    const weekEntry = weekMap.get(weekLabel) || {
      ...weekItem,
      week: weekLabel,
      classes: [],
    };
    const classMap = new Map(
      weekEntry.classes.map((classItem) => [classItem.className, classItem])
    );

    (weekItem.classes || []).forEach((classItem) => {
      const className = classItem?.className;
      if (!className) return;

      const existing = classMap.get(className);
      if (!existing) {
        classMap.set(className, {
          ...classItem,
          days: { ...(classItem.days || {}) },
          absentDays: { ...(classItem.absentDays || {}) },
          __mergeCount: 1,
        });
        return;
      }

      const mergeCount = existing.__mergeCount || 1;
      existing.days = existing.days || {};
      existing.absentDays = existing.absentDays || {};

      ATTENDANCE_DAY_KEYS.forEach((day) => {
        const presentValue = Number(existing.days[day] ?? 0);
        const incomingPresentValue = Number(classItem.days?.[day] ?? 0);
        const absentValue = Number(existing.absentDays[day] ?? 0);
        const incomingAbsentValue = Number(classItem.absentDays?.[day] ?? 0);

        existing.days[day] = Math.round(((presentValue * mergeCount) + incomingPresentValue) / (mergeCount + 1));
        existing.absentDays[day] = Math.round(((absentValue * mergeCount) + incomingAbsentValue) / (mergeCount + 1));
      });

      existing.__mergeCount = mergeCount + 1;
      classMap.set(className, existing);
    });

    weekEntry.classes = Array.from(classMap.values()).map(({ __mergeCount, ...classItem }) => classItem);
    weekMap.set(weekLabel, weekEntry);
  });

  return Array.from(weekMap.values()).sort((a, b) => getWeekNumber(a.week) - getWeekNumber(b.week));
};

const CalendarEventSidebar = ({ isDesktop, children }) => {
  const theme = useTheme();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [eventItems, setEventItems] = useState([]);
  const [allEventDates, setAllEventDates] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState({
    classes: false,
    event: false
  });
  const [formData, setFormData] = useState({
    title: '',
    time: '',
    audience: 'all',
    date: new Date(),
    classId: ''
  });

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        setLoading(prev => ({ ...prev, classes: true }));
        const userStr = localStorage.getItem('user');

        if (!userStr) {
          console.warn('User not found in localStorage');
          return;
        }

        const user = JSON.parse(userStr);
        const schoolId = user?.school?._id || user?.school?.id || user?.school;

        if (!schoolId) {
          console.warn('School ID not found in user object');
          return;
        }

        const res = await api.get(`/classes?schoolId=${schoolId}`);
        setClasses(res.data?.classes || []);
      } catch (err) {
        console.error('Error fetching classes:', err);
        setClasses([]);
      } finally {
        setLoading(prev => ({ ...prev, classes: false }));
      }
    };

    fetchClasses();
  }, []);

  const fetchEvents = async (date) => {
    try {
      setLoading(prev => ({ ...prev, event: true }));

      const res = await api.get(`/agenda?date=${format(date, 'yyyy-MM-dd')}`);

      // ✅ Normalize the shape to always be an array
      const items = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.agendas)
          ? res.data.agendas
          : Array.isArray(res.data?.data)
            ? res.data.data
            : [];

      setEventItems(items);
    } catch (error) {
      console.error('Error fetching event:', error);
      setEventItems([]);
    } finally {
      setLoading(prev => ({ ...prev, event: false }));
    }
  };



  const fetchAllEventDates = async () => {
    try {
      const res = await api.get(`/agenda/dates`);
      setAllEventDates(res.data || []);
    } catch (error) {
      console.error('Error fetching all event dates:', error);
      setAllEventDates([]);
    }
  };

  useEffect(() => {
    fetchEvents(selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    fetchAllEventDates();
  }, []);

  const handleOpenModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        title: item.title,
        time: item.time,
        audience: item.audience,
        date: parseISO(item.date),
        classId: item.classId?._id || ''
      });
    } else {
      setEditingItem(null);
      setFormData({
        title: '',
        time: '',
        audience: 'all',
        date: selectedDate,
        classId: ''
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingItem(null);
  };

  const handleSave = async () => {
    const payload = {
      ...formData,
      date: format(formData.date, 'yyyy-MM-dd')
    };

    if (formData.audience === 'class' && !formData.classId) {
      alert('Please select a class.');
      return;
    }

    try {
      if (editingItem) {
        await api.put(`/agenda/${editingItem._id}`, payload);
      } else {
        await api.post('/agenda', payload);
      }
      fetchEvents(selectedDate);
      fetchAllEventDates();
      handleCloseModal();
    } catch (err) {
      console.error('Error saving event:', err);
      alert('Failed to save event. Please try again.');
    }
  };

  const handleDelete = async (id) => {
    if (!id || id === 'undefined') {
      console.error('Cannot delete: event item has no valid ID');
      alert('Cannot delete this item — it has no valid ID. Try refreshing the page.');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this event item?')) {
      return;
    }

    try {
      await api.delete(`/agenda/${id}`);
      fetchEvents(selectedDate);
      fetchAllEventDates();
    } catch (err) {
      console.error('Error deleting event:', err);
      alert('Failed to delete event item.');
    }
  };

  const getAudienceLabel = (event) => {
    if (event.audience === 'class') {
      return `Class: ${event.class?.name || 'Unknown'}`;
    }
    return event.audience.charAt(0).toUpperCase() + event.audience.slice(1);
  };

  const CustomPickersDay = (props) => {
    const { day, selectedDay, ...other } = props;
    const match = allEventDates.find((d) => isSameDay(new Date(d.date), day));

    return (
      <PickersDay
        {...other}
        day={day}
        sx={{
          ...(match && {
            backgroundColor: `${eventColors[match.audience] || eventColors.all}33`,
            color: 'black',
            borderRadius: 2,
            '&:hover, &:focus': {
              backgroundColor: `${eventColors[match.audience] || eventColors.all}66`,
            },
            '&.Mui-selected': {
              backgroundColor: `${eventColors[match.audience] || eventColors.all}99`,
            },
          }),
        }}
      />
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {children ? (
        <Box sx={{
          position: isDesktop ? 'fixed' : 'static',
          right: isDesktop ? 0 : 'auto',
          top: isDesktop ? 80 : 'auto',
          width: isDesktop ? 300 : '100%',
          zIndex: isDesktop ? 1000 : 'auto',
          display: 'flex',
          justifyContent: isDesktop ? 'flex-end' : 'space-between',
          p: isDesktop ? 1 : 0,
          mb: isDesktop ? 0 : 2,
          mt: 0
        }}>
          {children}
        </Box>
      ) : null}

      <Box sx={{
        position: isDesktop ? 'fixed' : 'static',
        right: isDesktop ? 0 : 'auto', // Flush right
        top: isDesktop ? 130 : 'auto',
        width: isDesktop ? 300 : '100%', // Reduced width
        height: isDesktop ? 'calc(100vh - 110px)' : 'auto',
        overflowY: isDesktop ? 'auto' : 'visible',
        zIndex: 1000,
        backgroundColor: 'background.paper',
        borderTopLeftRadius: theme.shape.borderRadius * 3, // Match Charts
        borderBottomLeftRadius: theme.shape.borderRadius * 3, // Match Charts
        p: 1,
        border: '1px solid',
        borderColor: alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.55 : 0.7),
        borderRight: isDesktop ? 0 : undefined,
        boxShadow: 'none',
        backgroundImage: 'none',
        mt: isDesktop ? 0 : 3
      }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {format(selectedDate, 'MMMM yyyy')}
        </Typography>

        <DateCalendar
          value={selectedDate}
          onChange={(newDate) => setSelectedDate(newDate)}
          disablePast
          sx={{
            width: '100%',
            height: 'auto', // Allow height to grow
            flexGrow: 1, // Take available space
            '& .MuiPickersCalendarHeader-root': {
              pl: 2, pr: 2,
              mb: 2 // Add space below header
            },
            '& .MuiDayCalendar-header': {
              width: '100%',
              justifyContent: 'space-around',
              fontWeight: 'bold',
              mb: 1 // Add space below day names
            },
            '& .MuiDayCalendar-weekContainer': {
              width: '100%',
              justifyContent: 'space-around',
              my: 1.5 // Increase vertical gap between weeks
            },
            '& .MuiPickersDay-root': {
              fontSize: '1.0rem',
              width: 40,
              height: 40
            },
            '& .MuiDayCalendar-slideTransition': {
              minHeight: '320px', // Force minimum height for the days area
            }
          }}
          slots={{ day: CustomPickersDay }}
          slotProps={{
            day: { selectedDay: selectedDate },
            textField: {
              variant: 'outlined'
            }
          }}
        />

        <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1">Events</Typography>
          <IconButton
            onClick={() => handleOpenModal()}
            size="small"
            color="primary"
            aria-label="Add event item"
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>

        {loading.event ? (
          <LoadingState />
        ) : eventItems.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No events for this day.
          </Typography>
        ) : (
          eventItems.map((item, index) => {
            const color = eventColors[item.audience] || eventColors.all;
            return (
              <Box key={item._id || index} sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {item.time || 'All day'}
                </Typography>
                <Box sx={{
                  p: 1.5,
                  mt: 0.5,
                  borderRadius: 2,
                  backgroundColor: `${color}14`,
                  border: `1px solid ${alpha(color, 0.24)}`,
                  borderLeft: `3px solid ${color}`,
                  boxShadow: 'none'
                }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {getAudienceLabel(item)}
                      </Typography>
                      <Typography variant="body2">{item.title}</Typography>
                    </Box>
                    <Stack direction="row">
                      <Tooltip title="Edit">
                        <IconButton
                          onClick={() => handleOpenModal(item)}
                          size="small"
                          aria-label="Edit event item"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          onClick={() => handleDelete(item._id || item.id)}
                          size="small"
                          aria-label="Delete event item"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </Box>
              </Box>
            );
          })
        )}

        <Dialog open={modalOpen} onClose={handleCloseModal} fullWidth maxWidth="sm">
          <DialogTitle>{editingItem ? 'Edit Event' : 'Add Event'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                fullWidth
                label="Event Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                margin="normal"
              />

              <TextField
                fullWidth
                label="Event Description"
                value={formData.description || ""}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                margin="normal"
                multiline
                minRows={3}
                placeholder="Enter more details about this event..."
              />

              <DatePicker
                label="Event Date"
                value={formData.date}
                onChange={(newDate) => setFormData({ ...formData, date: newDate })}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: 'normal'
                  }
                }}
              />

              <TimePicker
                label="Time"
                value={formData.time ? new Date(`1970-01-01T${formData.time}`) : null}
                onChange={(newValue) => {
                  if (newValue) {
                    const formatted = format(newValue, 'HH:mm');
                    setFormData({ ...formData, time: formatted });
                  }
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: 'normal'
                  }
                }}
              />

              <FormControl fullWidth margin="normal">
                <InputLabel>Audience</InputLabel>
                <Select
                  value={formData.audience}
                  label="Audience"
                  onChange={(e) => setFormData({ ...formData, audience: e.target.value })}
                >
                  <MenuItem value="student">Student</MenuItem>
                  <MenuItem value="teacher">Teacher</MenuItem>
                  <MenuItem value="parent">Parent</MenuItem>
                  <MenuItem value="class">Class</MenuItem>
                  <MenuItem value="all">All</MenuItem>
                </Select>
              </FormControl>

              {formData.audience === 'class' && (
                <FormControl fullWidth margin="normal">
                  <InputLabel>Class</InputLabel>
                  <Select
                    value={formData.classId}
                    label="Class"
                    onChange={(e) => setFormData({ ...formData, classId: e.target.value })}
                    disabled={loading.classes}
                  >
                    {classes.map((cls) => (
                      <MenuItem key={cls._id} value={cls._id}>
                        {cls.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!formData.title || (formData.audience === 'class' && !formData.classId)}
            >
              {editingItem ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

const HomePage = () => {
  const theme = useTheme();

  useEffect(() => {
    ChartJS.defaults.color = theme.palette.text.secondary;
    ChartJS.defaults.borderColor = theme.palette.mode === 'dark' ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.1)';
  }, [theme.palette.mode, theme.palette.text.secondary]);

  const [classData, setClassData] = useState([]);
  const [gradeData, setGradeData] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [stats, setStats] = useState({
    students: 0, teachers: 0, classes: 0, assignments: 0, parents: 0, boys: 0, girls: 0, weekTracker: 0,
  });
  const [currentWeek, setCurrentWeek] = useState(null);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedWeek, setSelectedWeek] = useState('');
  const [teacherAttendanceChart, setTeacherAttendanceChart] = useState([]);

  const cardColors = {
    students: '#e3f2fd',
    teachers: '#e0f7fa',
    classes: '#e8f5e9',
    assignments: '#fff8e1',
    parents: '#f3e5f5',
    weekTracker: '#e6e6fa'
  };

  // Exercise chart state
  const [teacherExerciseData, setTeacherExerciseData] = useState([]);
  const [loadingExercises, setLoadingExercises] = useState(false);

  // Radar chart state
  const [radarChartData, setRadarChartData] = useState(null);
  const [loadingRadar, setLoadingRadar] = useState(false);

  // Class Performance Chart State (SBA Data)
  const [classPerformanceData, setClassPerformanceData] = useState([]);
  const [loadingClassPerformance, setLoadingClassPerformance] = useState(false);

  // Fees Collection State
  const [feesCollectionStats, setFeesCollectionStats] = useState([]);

  // Welcome modal state
  const [showWelcomeCard, setShowWelcomeCard] = useState(false);

  // Subscription Status State


  const closeWelcomeCard = async () => {
    setShowWelcomeCard(false);
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        let userObj = JSON.parse(userStr);
        userObj.hasSeenWelcome = true;
        localStorage.setItem('user', JSON.stringify(userObj));
      }
      await api.post('/profile/welcome-seen');
    } catch (err) {
      console.error("Error updating welcome status", err);
    }
  };

  const colorMode = useContext(ColorModeContext);
  const navigate = useNavigate();
  const isDesktop = useMediaQuery(theme.breakpoints.up('xl'));
  const chartPaperSx = useMemo(() => ({
    p: { xs: 1.75, sm: 2 },
    height: { xs: 460, sm: 380, xl: 420 },
    minWidth: 0,
    borderRadius: 2,
    border: '1px solid',
    borderColor: alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.55 : 0.7),
    boxShadow: 'none',
    backgroundImage: 'none',
  }), [theme.palette.divider, theme.palette.mode]);

  // Get user ID for notification filtering
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user'));
    } catch (e) {
      return null;
    }
  }, []);

  const { subscriptionStatus, showSubscriptionDialog, closeSubscriptionDialog,
    showPaymentConfirmedDialog, closePaymentConfirmedDialog } = useSubscriptionDialogs(user);

  const fetchCurrentWeek = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const schoolId = user?.school?._id || user?.school?.id || user?.school;

      if (!schoolId) {
        // console.warn("School ID not found in localStorage");
        return;
      }

      const response = await api.get(`/term/current-week?school=${schoolId}`);
      // console.log("Current week API response:", response.data);

      if (response.data) {
        setCurrentWeek(response.data);
      } else {
        console.error("No week data returned from API");
        setCurrentWeek({
          current: 0,
          total: 0,
          termName: 'No active term',
          academicYear: ''
        });
      }
    } catch (err) {
      console.error("Error fetching current week:", err);
      setCurrentWeek({
        current: 0,
        total: 0,
        termName: 'Error loading data',
        academicYear: ''
      });
    }
  };

  const fetchTeacherExerciseData = useCallback(async (overrideTermId) => {
    try {
      setLoadingExercises(true);
      const user = JSON.parse(localStorage.getItem("user"));
      const schoolId = user?.school?._id || user?.school?.id || user?.school;

      if (!schoolId) {
        // console.warn("School ID not available - skipping fetch");
        setTeacherExerciseData([]);
        return;
      }

      // ------------------------------------------------------------------
      // 1️⃣ & 2️⃣ GET ACADEMIC YEAR & TERM (Skip if overrideTermId provided)
      // ------------------------------------------------------------------
      let termId = overrideTermId || null;

      if (!termId) {
        let academicYear = '';
        try {
          const yearsRes = await api.get('/term/years', {
            params: { school: schoolId }
          });

          const years = yearsRes.data?.academicYears || [];
          if (years.length > 0) {
            academicYear = years[0];
          } else {
            console.warn("No academic years found");
            setTeacherExerciseData([]);
            return;
          }
        } catch (yearsErr) {
          console.error("Error fetching academic years:", yearsErr);
          setTeacherExerciseData([]);
          return;
        }

        try {
          const termsRes = await api.get('/term/all', {
            params: { school: schoolId, academicYear }
          });

          const termsData = termsRes.data?.allTerms || [];

          if (termsData.length > 0) {
            const activeTerm = termsData[0];
            termId = activeTerm._id;
          } else {
            console.warn("No terms found for academic year:", academicYear);
            setTeacherExerciseData([]);
            return;
          }
        } catch (termsErr) {
          console.error("Error fetching terms:", termsErr);
          setTeacherExerciseData([]);
          return;
        }
      }

      // ------------------------------------------------------------------
      // 3️⃣ FETCH EXERCISE SUMMARY FOR THE ENTIRE TERM
      // ------------------------------------------------------------------
      if (!localStorage.getItem('token')) return; // Avoid 401 on logout

      let allResults = [];
      try {
        const params = {
          school: schoolId,
          termId,
          page: 1,
          limit: 2000, // Large limit to get all term exercises
          populate: "teacher,class,term",
        };

        const res = await api.get("/exercises/summary", { params });

        if (res?.data) {
          if (res.data?.success && Array.isArray(res.data.data)) allResults = res.data.data;
          else if (Array.isArray(res.data.data)) allResults = res.data.data;
          else if (Array.isArray(res.data)) allResults = res.data;
          else if (Array.isArray(res.data?.data?.docs)) allResults = res.data.data.docs;
        }
      } catch (err) {
        console.error("Error fetching exercise summary for term:", err);
      }

      // ------------------------------------------------------------------
      // 4️⃣ AGGREGATE BY TEACHER  
      //     ⭐ UPDATED FOR MULTI-SUBJECT SUPPORT
      // ------------------------------------------------------------------
      const aggregated = Object.values(
        allResults.reduce((acc, item) => {
          const teacherObj = item.teacher;
          if (!teacherObj) return acc;

          const teacherId = teacherObj._id || teacherObj.id;
          if (!teacherId) return acc;

          if (!acc[teacherId]) {
            acc[teacherId] = {
              teacher: {
                id: teacherId,
                name: teacherObj.user?.name || teacherObj.name || "Unknown",
                subjects: Array.isArray(teacherObj.subjects)
                  ? teacherObj.subjects.map(s => s.name || s.shortName).join(", ")
                  : "No Subjects",
              },
              class: item.class || null,
              term: item.term || null,
              totalExercises: 0,
              subjectBreakdown: [],
              weeks: new Set(),
              classes: new Set()
            };
          }

          acc[teacherId].totalExercises += Number(item.totalExercises || 0);

          if (item.week) acc[teacherId].weeks.add(item.week);

          if (item.class && item.class.name) {
            const className = item.class.level ? `${item.class.name} (${item.class.level})` : item.class.name;
            acc[teacherId].classes.add(className);
          } else if (item.class) {
            acc[teacherId].classes.add(`Class ${item.class}`);
          }

          if (Array.isArray(item.subjectCounts)) {
            item.subjectCounts.forEach(sc => {
              const subjectName = sc.subject?.name || sc.subject?.shortName || sc.subject || 'Unknown Subject';
              let classNameStr = 'N/A';
              if (item.class && item.class.name) {
                classNameStr = item.class.level ? `${item.class.name} (${item.class.level})` : item.class.name;
              } else if (item.class) {
                classNameStr = `Class ${item.class}`;
              }

              const breakdownLabel = `${subjectName} (Class: ${classNameStr})`;
              const existing = acc[teacherId].subjectBreakdown.find(b => b.name === breakdownLabel);
              if (existing) existing.count += Number(sc.count || 0);
              else acc[teacherId].subjectBreakdown.push({ name: breakdownLabel, count: Number(sc.count || 0) });
            });
          }

          return acc;
        }, {})
      );

      aggregated.forEach(agg => {
        agg.weeksStr = Array.from(agg.weeks).join(', ');
        delete agg.weeks;

        agg.classesStr = Array.from(agg.classes).join(', ');
        delete agg.classes;
      });

      setTeacherExerciseData(aggregated || []);

    } catch (err) {
      if (err.response?.status !== 401) {
        console.error("Error fetching teacher exercise data:", err);
      }
      setTeacherExerciseData([]);
    } finally {
      setLoadingExercises(false);
    }
  }, []);

  // ---------------------------
  // Fetch Radar Chart Data (Average Scores per Subject)
  // ---------------------------
  const fetchRadarChartData = async (termId) => {
    try {
      setLoadingRadar(true);

      const user = JSON.parse(localStorage.getItem('user'));
      const schoolId = user?.school?._id || user?.school?.id || user?.school;

      if (!schoolId) {
        console.warn('⚠️ School ID not found. Skipping radar chart fetch.');
        return;
      }

      // Fetch data from backend (school-scoped)
      const res = await api.get('/quizzes/data/radar-chart', {
        params: { schoolId, termId }
      });

      const data = res.data;

      if (Array.isArray(data) && data.length > 0) {
        // Extract subjects and scores for chart
        const subjects = data.map(item => item.subject || 'Unknown');
        const scores = data.map(item => item.avgScore || 0);

        setRadarChartData({
          labels: subjects,
          datasets: [
            {
              label: 'Average Score (%)',
              data: scores,
              backgroundColor: 'rgba(66, 165, 245, 0.12)',   // translucent blue fill
              borderColor: 'rgba(66, 165, 245, 1)',         // solid blue line
              borderWidth: 1.25,
              pointBackgroundColor: 'rgba(66, 165, 245, 1)',
              pointBorderColor: '#fff',
              pointRadius: 2,
              pointHoverRadius: 4,
              pointBorderWidth: 1,
              pointHoverBackgroundColor: '#fff',
              pointHoverBorderColor: 'rgba(66, 165, 245, 1)',
            },
          ],
        });
      } else {
        console.warn('ℹ️ No radar chart data available for this school.');
        setRadarChartData({
          labels: [],
          datasets: [],
        });
      }
    } catch (err) {
      console.error('❌ Error fetching radar chart data:', err.response?.data || err.message);
      setRadarChartData({
        labels: [],
        datasets: [],
      });
    } finally {
      setLoadingRadar(false);
    }
  };

  // ✅ Class Performance Data Fetching (SBA Averages)
  const fetchClassPerformanceData = async (termId) => {
    try {
      setLoadingClassPerformance(true);
      const user = JSON.parse(localStorage.getItem('user'));
      const schoolId = user?.school?._id || user?.school?.id || user?.school;

      if (!schoolId || typeof schoolId !== 'string') {
        console.warn('⚠️ School ID not available or invalid');
        return;
      }

      const params = { schoolId, termId };
      let v2Data = [];
      let legacyData = [];

      try {
        const v2Res = await api.get('/sba-v2/class-averages-chart', { params });
        v2Data = v2Res.data?.data || [];
      } catch (v2Err) {
        console.warn('⚠️ SBA V2 class performance unavailable, falling back to legacy SBA:', v2Err.message);
      }

      try {
        const legacyRes = await api.get('/sba/class-averages-chart', { params });
        legacyData = legacyRes.data?.data || [];
      } catch (legacyErr) {
        console.warn('⚠️ Legacy SBA class performance unavailable:', legacyErr.message);
        if (!v2Data.length) {
          throw legacyErr;
        }
      }

      const mergedByClass = new Map();
      legacyData.forEach((item) => mergedByClass.set(String(item.classId), item));
      v2Data.forEach((item) => mergedByClass.set(String(item.classId), item));

      const mergedData = Array.from(mergedByClass.values()).sort(
        (a, b) => (Number(b.average) || 0) - (Number(a.average) || 0)
      );

      setClassPerformanceData(mergedData);
    } catch (err) {
      console.error('❌ Error fetching class performance data:', err);
      setClassPerformanceData([]);
    } finally {
      setLoadingClassPerformance(false);
    }
  };

  // ✅ Refactored: Fetch dashboard data depending on current term
  useEffect(() => {
    // Basic stats don't depend on term
    fetchSummaryStats();
    fetchCurrentWeek();

    // Welcome logic
    const checkWelcome = () => {
      try {
        const userStr = localStorage.getItem('user');
        if (!userStr) return;
        const userObj = JSON.parse(userStr);
        if (userObj.hasSeenWelcome) return;
        setShowWelcomeCard(true);
      } catch (err) {
        console.error("Welcome logic error", err);
      }
    };
    checkWelcome();
  }, []);

  // ✅ Fetch term-dependent data when termId is available
  useEffect(() => {
    if (currentWeek) {
      // console.log("Fetching term-specific data for:", currentWeek.termId);
      const termId = currentWeek.termId;

      fetchDashboard(termId, currentWeek.current);
      fetchTeacherDailyChart(termId);
      fetchTeacherExerciseData(termId);
      fetchRadarChartData(termId); // Assuming we want this per term too? If not, remove arg.
      fetchClassPerformanceData(termId); // Pass termId to filter SBA data
    }
  }, [currentWeek]); // Runs when currentWeek updates

  const fetchDashboard = async (termId, currentWeekNum) => {
    try {
      const params = termId ? { params: { termId } } : {};

      const [classRes, gradeRes, attendanceRes, feesRes] = await Promise.all([
        api.get('/dashboard/students-by-class'), // No params needed for current class counts
        api.get('/dashboard/average-grades', params),    // Check if this endpoint supports termId
        api.get('/dashboard/weekly-attendance', params),
        api.get('/dashboard/fees-collection', params),
      ]);
      const classes = Array.isArray(classRes.data) ? classRes.data : [];
      const weeklyAttendance = normalizeWeeklyAttendance(
        Array.isArray(attendanceRes.data) ? attendanceRes.data : []
      );

      setClassData(classes);
      setGradeData(gradeRes.data);
      setAttendanceData(weeklyAttendance);
      setFeesCollectionStats(feesRes.data || []);

      const availableWeekLabels = weeklyAttendance
        .map((item) => formatWeekLabel(item.week))
        .filter(Boolean);
      const preferredWeekLabel = currentWeekNum ? formatWeekLabel(currentWeekNum) : '';
      const selectedWeekLabel = preferredWeekLabel && availableWeekLabels.includes(preferredWeekLabel)
        ? preferredWeekLabel
        : availableWeekLabels[availableWeekLabels.length - 1] || '';
      const selectedWeekData = weeklyAttendance.find(
        (item) => formatWeekLabel(item.week) === selectedWeekLabel
      );
      const firstAttendanceClass = selectedWeekData?.classes?.[0]?.className;
      const firstDashboardClass = classes[0]?.className;

      setSelectedWeek(selectedWeekLabel);
      setSelectedClass(firstAttendanceClass || firstDashboardClass || '');
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    }
  };

  const fetchSummaryStats = async () => {
    try {
      const [studentsRes, teachersRes, classesRes, assignmentsRes, parentsRes] = await Promise.all([
        api.get('/students'),
        api.get('/teachers'),
        api.get('/classes'),
        api.get('/assignments'),
        api.get('/parents'),
      ]);

      const allStudents = studentsRes.data;
      const allTeachers = teachersRes.data.teachers || [];
      const allClasses = classesRes.data?.classes || [];
      const allAssignments = assignmentsRes.data;
      const allParents = parentsRes.data;

      const boys = allStudents.filter(s => s.gender?.toLowerCase() === 'male').length;
      const girls = allStudents.filter(s => s.gender?.toLowerCase() === 'female').length;

      setStats({
        students: allStudents.length,
        teachers: allTeachers.length,
        classes: allClasses.length,
        assignments: allAssignments.length,
        parents: allParents.length,
        boys,
        girls
      });
    } catch (err) {
      console.error('Stats fetch error:', err);
    }
  };

  const fetchTeacherDailyChart = async (termId) => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));

      if (!userData?.school) {
        throw new Error('School ID not found in user data');
      }

      const schoolId = userData.school?._id || userData.school?.id || userData.school;

      const res = await api.get('/attendance/teacher/admin/daily-records', {
        params: {
          school: schoolId,
          termId: termId // ✅ Pass termId
        }
      });
      setTeacherAttendanceChart(res.data?.data?.weeklyChart || []);
    } catch (err) {
      console.error('Error fetching teacher attendance:', err);
    }
  };

  const exercisePolarData = useMemo(() => {
    // Safely handle data (always convert to array)
    const exercises = Array.isArray(teacherExerciseData)
      ? teacherExerciseData
      : teacherExerciseData
        ? [teacherExerciseData]
        : [];

    // Handle empty state
    if (exercises.length === 0) {
      return {
        labels: ['No data available'],
        datasets: [{
          data: [1],
          backgroundColor: ['#f5f5f5'],
          borderColor: '#e0e0e0',
          borderWidth: 1
        }]
      };
    }

    // Improved color generation
    const generateColors = (count) => {
      const baseColors = [
        '#8aa0c7', // Red
        '#c7f2f2', // Blue
        '#9ccfe9', // Yellow
        '#4BC0C0', // Teal
        '#9966FF', // Purple
        '#FF9F40', // Orange
        '#8AC24A', // Green
        '#FF6B6B'  // Coral
      ];

      // Reuse colors if more items than base colors
      return Array.from({ length: count }, (_, i) =>
        baseColors[i % baseColors.length]
      );
    };

    // Process data
    return {
      labels: exercises.map(item => {
        if (item.teacher?.name) return item.teacher.name;
        if (item.teacher) return `Teacher ${item.teacher._id || item.teacher}`;
        return 'Unknown Teacher';
      }),
      datasets: [{
        data: exercises.map(item => item.totalExercises || 0),
        backgroundColor: generateColors(exercises.length),
        borderColor: '#ffffff',
        borderWidth: 1,
        hoverBorderWidth: 1,
        hoverBackgroundColor: generateColors(exercises.length).map(color =>
          color.replace(/\)$/, ', 0.8)').replace('rgb', 'rgba')
        ),
        hoverBorderColor: '#ffffff',
        barPercentage: 0.32,
        categoryPercentage: 0.46
      }]
    };
  }, [teacherExerciseData]);

  const attendanceChartData = useMemo(() => {
    const currentWeek = attendanceData.find(
      d => formatWeekLabel(d.week) === formatWeekLabel(selectedWeek)
    );
    const clsData = currentWeek?.classes?.find(c => c.className === selectedClass);

    const days = ['M', 'T', 'W', 'TH', 'F'];
    const labels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
    const present = days.map(day => clsData?.days?.[day] ?? 0);
    const absent = days.map(day => clsData?.absentDays?.[day] ?? 0);
    return {
      labels,
      datasets: [
        {
          label: 'Total Present',
          data: present,
          backgroundColor: theme.palette.mode === 'dark' ? theme.palette.primary.main : '#8aa0c7',
          categoryPercentage: 0.42,
          barPercentage: 0.3,
          borderRadius: 8,
          maxBarThickness: 20
        },
        {
          label: 'Total Absent',
          data: absent,
          backgroundColor: theme.palette.mode === 'dark' ? theme.palette.warning.main : '#A0D2EB',
          categoryPercentage: 0.42,
          barPercentage: 0.3,
          borderRadius: 8,
          maxBarThickness: 20
        }
      ]
    };
  }, [attendanceData, selectedWeek, selectedClass]);

  const attendanceClassOptions = useMemo(() => {
    const selectedWeekData = attendanceData.find(
      d => formatWeekLabel(d.week) === formatWeekLabel(selectedWeek)
    );
    const attendanceClasses = selectedWeekData?.classes?.map((c) => c.className).filter(Boolean) || [];
    const dashboardClasses = classData.map((c) => c.className).filter(Boolean);

    return Array.from(new Set([...attendanceClasses, ...dashboardClasses]));
  }, [attendanceData, selectedWeek, classData]);

  const attendanceWeekOptions = useMemo(
    () => Array.from(new Set(attendanceData.map((item) => formatWeekLabel(item.week)).filter(Boolean))),
    [attendanceData]
  );

  const handleAttendanceWeekChange = useCallback((week) => {
    const nextWeek = formatWeekLabel(week);
    const nextWeekData = attendanceData.find(
      d => formatWeekLabel(d.week) === nextWeek
    );
    const nextClass = nextWeekData?.classes?.[0]?.className;

    setSelectedWeek(nextWeek);
    if (nextClass) setSelectedClass(nextClass);
  }, [attendanceData]);


  const teacherBarData = useMemo(() => {
    const labels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
    const values = teacherAttendanceChart.map(day => day.presentPercentage || 0);
    return {
      labels,
      datasets: [{
        label: 'Teacher Present (%)',
        data: values,
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.primary.light : '#b2c9e7',
        borderRadius: 8,
        barPercentage: 0.3,
        categoryPercentage: 0.38,
        maxBarThickness: 18
      }]
    };
  }, [teacherAttendanceChart]);

  const studentClassDoughnutData = useMemo(() => ({
    labels: classData.map(c => c.className),
    datasets: [{
      data: classData.map(c => c.count),
      backgroundColor: ['#8aa0c7', '#b2c9e7', '#9ccfe9', '#c7f2f2', '#ADC4C1'],
      borderWidth: 1,
      hoverBorderWidth: 1,
      borderRadius: 8
    }]
  }), [classData]);

  const classPerformanceLineData = useMemo(() => ({
    labels: classPerformanceData.map(c => c.className),
    datasets: [{
      label: 'Avg Grade (%)',
      data: classPerformanceData.map(c => c.average),
      borderColor: classPerformanceData.map(c =>
        c.average >= 85 ? '#06D6A0' :
          c.average >= 70 ? '#FFD166' :
            '#EF476F'
      ),
      backgroundColor: classPerformanceData.map(c =>
        c.average >= 85 ? '#06D6A033' :
          c.average >= 70 ? '#FFD16633' :
            '#EF476F33'
      ),
      borderWidth: 1.5,
      pointBackgroundColor: classPerformanceData.map(c =>
        c.average >= 85 ? '#06D6A0' :
          c.average >= 70 ? '#FFD166' :
            '#EF476F'
      ),
      pointRadius: 3,
      pointHoverRadius: 5,
      tension: 0.35,
      fill: true
    }]
  }), [classPerformanceData]);

  const feesCollectionChartData = useMemo(() => {
    // If feesCollectionStats has data, we map over it
    // Otherwise fallback logic for empty state
    const labels = feesCollectionStats.length > 0
      ? feesCollectionStats.map(c => c.className)
      : classData.length > 0 ? classData.map(c => c.className) : [];

    // Generate real fee data or 0 if no results
    const totalFees = feesCollectionStats.length > 0
      ? feesCollectionStats.map(c => c.totalFees || 0)
      : labels.map(() => 0);

    const collectedFees = feesCollectionStats.length > 0
      ? feesCollectionStats.map(c => c.collectedFees || 0)
      : labels.map(() => 0);

    return {
      labels,
      datasets: [
        {
          label: 'Total Fee',
          data: totalFees,
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.15)' : '#eaf0f9',
          borderRadius: 6,
          barPercentage: 0.24,
          categoryPercentage: 0.44,
          maxBarThickness: 24,
          grouped: false, // Ensures the bars overlap
          order: 2, // Drawn in the background
        },
        {
          label: 'Collected Fee',
          data: collectedFees,
          backgroundColor: theme.palette.mode === 'dark' ? theme.palette.primary.main : '#b2c9e7',
          borderRadius: 6,
          barPercentage: 0.24,
          categoryPercentage: 0.44,
          maxBarThickness: 24,
          grouped: false,
          order: 1, // Drawn on top
        }
      ]
    };
  }, [classData, feesCollectionStats]);


  const radarChartOptions = useMemo(() => ({
    maintainAspectRatio: false,
    responsive: true,
    layout: { padding: 0 },
    plugins: { legend: { display: false } },
    scales: {
      r: {
        beginAtZero: true,
        max: 100,
        pointLabels: {
          font: { size: 10, weight: 'bold' },
          padding: 5,
          color: theme.palette.text.secondary,
          callback: function (label) {
            return label.length > 15 ? label.substr(0, 15) + '...' : label;
          }
        },
        ticks: { display: false, stepSize: 20, backdropPadding: 0 }
      }
    }
  }), [theme.palette.text.secondary]);

  const ExercisePolarChart = () => (
    <Paper elevation={0} sx={{
      ...chartPaperSx,
      display: 'flex',
      flexDirection: 'column',
      position: 'relative'
    }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography fontWeight={600}>📚 Exercises by Teacher</Typography>
        <Tooltip title="Refresh data">
          <span>
            <IconButton
              size="small"
              onClick={fetchTeacherExerciseData}
              disabled={loadingExercises}
              sx={{ position: 'absolute', top: 8, right: 8 }}
            >
              <Refresh fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {loadingExercises ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="80%">
          <CircularProgress size={40} />
          <Typography variant="body2" sx={{ ml: 2 }}>Loading exercise data...</Typography>
        </Box>
      ) : teacherExerciseData.length > 0 ? (
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {/* Chart canvas — grows to fill all available space */}
          <Box sx={{ flexGrow: 1, position: 'relative', minHeight: 0 }}>
            <PolarArea
              data={exercisePolarData}
              options={{
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    callbacks: {
                      label: (context) => {
                        const label = context.label || '';
                        const value = context.raw || 0;
                        const index = context.dataIndex;

                        // Safely access item
                        const item = teacherExerciseData?.[index] || {};

                        const teacherSubjects = item.teacher?.subjects || 'N/A';

                        let details = [
                          `Subjects Taught: ${teacherSubjects}`,
                          `Classes: ${item.classesStr || 'N/A'}`,
                          `Weeks Submitted: ${item.weeksStr || (item.week || 'None')}`,
                          `Total Exercises: ${value}`
                        ];

                        if (item.subjectBreakdown && item.subjectBreakdown.length > 0) {
                          details.push('--- Breakdown ---');
                          item.subjectBreakdown.forEach(sb => {
                            details.push(`${sb.name}: ${sb.count}`);
                          });
                        }

                        return details;
                      },
                      title: (items) => {
                        return items[0].label;
                      }
                    }
                  }
                },
                scales: {
                  r: {
                    ticks: {
                      display: false,
                      stepSize: 2
                    },
                    grid: {
                      color: 'rgba(0, 0, 0, 0.1)'
                    },
                    pointLabels: {
                      display: false
                    }
                  }
                },
                animation: {
                  animateRotate: true,
                  animateScale: true
                },
                responsive: true
              }}
            />
          </Box>

          {/* Custom compact scrollable legend — never shrinks the chart */}
          <Box
            sx={{
              flexShrink: 0,
              maxHeight: 78,
              overflowY: 'auto',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '4px 10px',
              pt: 0.75,
              mt: 0.5,
              borderTop: '1px solid',
              borderColor: 'divider',
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-thumb': { borderRadius: 2, bgcolor: 'divider' }
            }}
          >
            {exercisePolarData.labels.map((label, i) => {
              const color = exercisePolarData.datasets[0].backgroundColor[i];
              const value = exercisePolarData.datasets[0].data[i];
              const truncated = label.length > 14 ? label.slice(0, 13) + '\u2026' : label;
              return (
                <Tooltip key={i} title={`${label} \u2014 ${value} exercise${value !== 1 ? 's' : ''}`} arrow>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'default' }}>
                    <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ fontSize: 10, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                      {truncated}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      ) : (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ flexGrow: 1 }}>
          <Typography variant="body2" color="text.secondary">
            No exercise data available
          </Typography>
        </Box>
      )}
    </Paper>
  );

  return (
    <Box className="workspace-overview" sx={{ paddingRight: isDesktop ? '310px' : 0, position: 'relative', pt: { xs: 0.5, sm: 0 } }}>
      <PageHeader title="Dashboard Overview" description="Monitor your school’s enrollment, attendance, learning, and daily activity." />

      <Dialog
        open={showWelcomeCard}
        onClose={() => { }} // Disabled automatic close so they have to click the button
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            width: '85%',
            maxWidth: 420,
            backgroundImage: 'none',
            boxShadow: theme.shadows[20]
          }
        }}
      >
        <Box sx={{
          p: 4,
          textAlign: 'center',
          background: 'linear-gradient(135deg, #2a2f5a 0%, #1A2980 100%)',
          color: 'white'
        }}>
          <Box sx={{ width: 80, height: 80, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.2)', display: 'inline-flex', justifyContent: 'center', alignItems: 'center', mb: 2, border: '2px solid rgba(255,255,255,0.5)' }}>
            <Brightness7 sx={{ fontSize: 40, color: '#fff' }} />
          </Box>
          <Typography variant="h4" fontWeight="800" sx={{ letterSpacing: 0.5 }} gutterBottom>Hello {user?.name || user?.user?.name || 'Admin'}</Typography>
          <Typography variant="subtitle1" fontWeight="500" sx={{ opacity: 0.9 }}>
            Welcome to {user?.school?.name || user?.schoolName || user?.institution || 'School Management System'}
          </Typography>
        </Box>
        <DialogContent sx={{ p: '24px !important', textAlign: 'center' }}>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.6 }}>
            Your account has been successfully set up. Access your dashboard to manage your entire institution efficiently. We are thrilled to have you onboard! 🎉✨
          </Typography>
          <Button
            fullWidth
            variant="contained"
            onClick={closeWelcomeCard}
            sx={{
              py: 1.8,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #1A2980 0%, #2a2f5a 100%)',
              fontWeight: 'bold',
              letterSpacing: 0.5,
              fontSize: '1.05rem',
              textTransform: 'none',
              boxShadow: '0 4px 14px 0 rgba(0,0,0,0.2)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1A2980 0%, #2a2f5a 100%)',
                opacity: 0.9
              }
            }}
          >
            Let's Get Started
          </Button>
        </DialogContent>
      </Dialog>

      {/* 🚨 Subscription Warning Dialog (Pop-up) */}
      <Dialog
        open={showSubscriptionDialog}
        onClose={closeSubscriptionDialog}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
            borderTop: `6px solid ${theme.palette.error.main}`
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main', fontWeight: 'bold' }}>
          <NotificationImportant color="error" />
          Outstanding Subscription Access Fee
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>
            Your school has an outstanding balance of <strong>₵{subscriptionStatus.amountOwed.toFixed(2)}</strong>.
            {subscriptionStatus.dueDate && (
              <>
                <br />
                Payment was expected by <strong>{new Date(subscriptionStatus.dueDate).toLocaleDateString()}</strong>.
              </>
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please arrange for payment to avoid service interruption and ensure continued access to all platform features.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
          <Button
            onClick={closeSubscriptionDialog}
            variant="contained"
            color="error"
            sx={{ px: 4, borderRadius: 2 }}
          >
            I Understand
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🎉 Payment Confirmed Dialog (Pop-up) */}
      <Dialog
        open={showPaymentConfirmedDialog}
        onClose={closePaymentConfirmedDialog}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
            borderTop: `6px solid ${theme.palette.success.main}`
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main', fontWeight: 'bold' }}>
          <CheckCircle color="success" />
          Payment Confirmed
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="h6" gutterBottom>
            Thank you!
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Your invoice of <strong>₵{subscriptionStatus.lastPaid?.amount?.toFixed(2)}</strong> has been successfully marked as <strong>PAID</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            A receipt is attached in your billing dashboard for your records. We appreciate your continued partnership! 🚀
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
          <Button
            onClick={closePaymentConfirmedDialog}
            variant="contained"
            color="success"
            sx={{ px: 4, borderRadius: 2 }}
          >
            Great, Thanks!
          </Button>
        </DialogActions>
      </Dialog>


      {/* 5 Cards with responsive layout */}
      <SummaryStrip container spacing={2} columns={{ xs: 12, sm: 12, md: 12, xl: 10 }} mt={1} mb={2}>
        <Grid size={{ xs: 6, sm: 6, md: 4, xl: 2 }}>
          <StatCard
            title="Total Students"
            count={stats.students}
            icon={<People />}
            subtitle={`Boys: ${stats.boys}, Girls: ${stats.girls}`}
            themeColor="primary"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 4, xl: 2 }}>
          <StatCard
            title="Current Week"
            count={currentWeek?.current}
            icon={<Event />}
            isWeekTracker
            weekData={currentWeek}
            themeColor="info"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 4, xl: 2 }}>
          <StatCard
            title="Teachers"
            count={stats.teachers}
            icon={<School />}
            themeColor="secondary"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 4, xl: 2 }}>
          <StatCard
            title="Classes"
            count={stats.classes}
            icon={<Class />}
            themeColor="success"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, xl: 2 }}>
          <StatCard
            title="Assignments"
            count={stats.assignments}
            icon={<Assignment />}
            themeColor="warning"
          />
        </Grid>
      </SummaryStrip>

      {/* Main Content Area */}
      <Box component="main" sx={{ flexGrow: 1, p: { xs: 0, sm: 1, lg: 2 }, mt: 2, width: '100%', minWidth: 0 }}>

        {/* 🚨 Subscription Warning Banner removed for Dialog */}

        <Grid container spacing={{ xs: 2, md: 3 }}>
          {/* Top Welcome Section / Students per Class */}
          <Grid size={{ xs: 12, md: 6, xl: 4 }}>
            <Paper elevation={0} sx={{
              ...chartPaperSx,
              display: 'flex',
              flexDirection: 'column'
            }}>
              <Typography fontWeight={600}>🎓 Students per Class</Typography>
              <Box sx={{ flexGrow: 1, position: 'relative', width: '100%', minHeight: 0 }}>
                <Doughnut
                  data={studentClassDoughnutData}
                  options={{
                    maintainAspectRatio: false,
                    responsive: true,
                    cutout: '50%',
                    layout: {
                      padding: 0
                    },
                    plugins: {
                      legend: {
                        display: true,
                        position: 'top',
                        align: 'center',
                        labels: {
                          usePointStyle: true,
                          boxWidth: 8,
                          padding: 10,
                          font: {
                            size: 11
                          }
                        }
                      },
                      tooltip: {
                        callbacks: {
                          label: (ctx) => {
                            const data = classData[ctx.dataIndex];
                            if (!data) return '';
                            return [
                              ` Class: ${data.className}`,
                              ` 👦 Boys: ${data.boys || 0}`,
                              ` 👧 Girls: ${data.girls || 0}`,
                              ` 👥 Total: ${data.count}`
                            ];
                          }
                        }
                      }
                    }
                  }}
                />
              </Box>
            </Paper>
          </Grid>

          {/* Student Weekly Attendance */}
          <Grid size={{ xs: 12, md: 6, xl: 4 }}>
            <Paper elevation={0} sx={chartPaperSx}>
              <Typography fontWeight={600} mb={2}>📊 Student Weekly Attendance</Typography>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1} sx={{ width: '100%' }}>
                <TextField
                  select size="small" label="Week"
                  value={selectedWeek || ''} onChange={(e) => handleAttendanceWeekChange(e.target.value)}
                  sx={{ minWidth: 100 }}
                >
                  {attendanceWeekOptions.length > 0 ? (
                    attendanceWeekOptions.map((weekLabel) => (
                      <MenuItem key={weekLabel} value={weekLabel}>
                        {weekLabel}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="">No attendance</MenuItem>
                  )}
                </TextField>

                <TextField
                  select size="small" label="Class"
                  value={selectedClass || ''} onChange={(e) => setSelectedClass(e.target.value)}
                  sx={{ minWidth: 100 }}
                >
                  {attendanceClassOptions.map((className, i) => (
                    <MenuItem key={`${className}-${i}`} value={className}>{className}</MenuItem>
                  ))}
                </TextField>
              </Stack>
              <Bar
                data={attendanceChartData}
                options={{
                  maintainAspectRatio: false,
                  layout: { padding: { top: 10, bottom: 100 } },
                  plugins: {
                    legend: { display: true, position: 'top', labels: { font: { size: 10 } } },
                    tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y}%` } }
                  },
                  scales: {
                    x: { stacked: false, ticks: { font: { size: 10 } } },
                    y: { beginAtZero: true, max: 100, ticks: { stepSize: 20 } }
                  }
                }}
              />
            </Paper>
          </Grid>

          {/* Daily Teacher Attendance */}
          <Grid size={{ xs: 12, md: 6, xl: 4 }}>
            <Paper elevation={0} sx={chartPaperSx}>
              <Typography fontWeight={600}>👨🏫 Daily Teacher Attendance</Typography>
              <Bar
                data={teacherBarData}
                options={{
                  maintainAspectRatio: false,
                  layout: {
                    padding: {
                      top: 10,
                      bottom: 40,
                    },
                  },
                  plugins: {
                    legend: {
                      display: true,
                      position: 'top',
                      labels: {
                        font: { size: 10 }
                      }
                    },
                    tooltip: {
                      callbacks: {
                        label: ctx => `${ctx.parsed.y}% Present`
                      }
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      max: 100,
                      ticks: { stepSize: 20 }
                    },
                    x: {
                      ticks: { font: { size: 10 } }
                    }
                  }
                }}
              />
            </Paper>
          </Grid>

          {/* Class Performance Chart */}
          <Grid size={{ xs: 12, md: 6, xl: 4 }}>
            <Paper elevation={0} sx={chartPaperSx}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography fontWeight={600}>📚 Class Performance</Typography>
                <Tooltip title="Refresh class performance data">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => fetchClassPerformanceData(currentWeek?.termId)}
                      disabled={loadingClassPerformance}
                    >
                      <Refresh fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              {loadingClassPerformance ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="80%">
                  <CircularProgress />
                  <Typography variant="body2" sx={{ ml: 2 }}>
                    Loading class performance data...
                  </Typography>
                </Box>
              ) : classPerformanceData.length > 0 ? (
                <Line
                  data={classPerformanceLineData}
                  options={{
                    maintainAspectRatio: false,
                    layout: { padding: { top: 10, bottom: 30 } },
                    scales: {
                      y: {
                        beginAtZero: true,
                        max: 100,
                        ticks: { stepSize: 20 },
                        grid: {
                          color: 'rgba(0, 0, 0, 0.1)'
                        }
                      },
                      x: {
                        ticks: { font: { size: 10 } },
                        grid: {
                          color: 'rgba(0, 0, 0, 0.1)'
                        }
                      }
                    },
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          label: (ctx) => `Average: ${ctx.parsed.y}%`,
                          afterLabel: (context) => {
                            const data = classPerformanceData[context.dataIndex];
                            const avg = context.parsed.y;
                            let performanceText = '';

                            if (avg >= 85) performanceText = '🎉 Excellent';
                            else if (avg >= 70) performanceText = '👍 Good';
                            else if (avg >= 60) performanceText = '📚 Needs Improvement';
                            else performanceText = '⚠️ Requires Attention';

                            return [
                              `Subjects: ${data.subjectCount || 'N/A'}`,
                              `Students: ${data.studentCount || 'N/A'}`,
                              performanceText
                            ];
                          }
                        }
                      }
                    },
                    elements: {
                      line: {
                        fill: 'start'
                      }
                    }
                  }}
                />
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height="80%">
                  <Typography variant="body2" color="text.secondary">
                    No class performance data available.
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Online Quiz Performance */}
          <Grid size={{ xs: 12, md: 6, xl: 4 }}>
            <Paper elevation={0} sx={{
              ...chartPaperSx,
              display: 'flex',
              flexDirection: 'column'
            }}>
              <Typography fontWeight={600} sx={{ mb: 1 }}>📊 Online Quiz Performance</Typography>
              {loadingRadar ? (
                <LoadingState />
              ) : radarChartData && radarChartData.datasets && radarChartData.datasets.length > 0 ? (
                <Box sx={{ flexGrow: 1, position: 'relative', width: '100%', minHeight: 0 }}>
                  <Radar
                    data={radarChartData}
                    options={radarChartOptions}
                  />
                </Box>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height="80%">
                  <Typography variant="body2" color="text.secondary">
                    No online quiz data available
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Exercises by Teacher */}
          <Grid size={{ xs: 12, md: 6, xl: 4 }}>
            <ExercisePolarChart />
          </Grid>
        </Grid>

        {/* Fees Collection Full-Width Chart (Only for Private Schools) */}
        {(!user?.school?.schoolType || user.school.schoolType.toLowerCase() === 'private') && (
          <Box sx={{ mt: 3, width: '100%' }}>
            <Paper elevation={0} sx={{
              ...chartPaperSx,
              display: 'flex',
              flexDirection: 'column'
            }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography fontWeight={600}>💰 Fees Collection</Typography>
                <TextField
                  select
                  size="small"
                  value="Term"
                  onChange={() => { }}
                  variant="standard"
                  InputProps={{ disableUnderline: true, sx: { fontSize: '0.9rem', fontWeight: 'bold' } }}
                >
                  <MenuItem value="Term">This Term</MenuItem>
                  <MenuItem value="Month">This Month</MenuItem>
                  <MenuItem value="Year">This Year</MenuItem>
                </TextField>
              </Stack>

              <Box sx={{ flexGrow: 1, position: 'relative', width: '100%', minHeight: 0 }}>
                <Bar
                  data={feesCollectionChartData}
                  options={{
                    maintainAspectRatio: false,
                    responsive: true,
                    layout: { padding: { top: 10, bottom: 20 } },
                    plugins: {
                      legend: {
                        display: true,
                        position: 'top',
                        align: 'center',
                        labels: {
                          usePointStyle: true,
                          boxWidth: 8,
                          font: { size: 12 },
                          padding: 20
                        }
                      },
                      tooltip: {
                        callbacks: {
                          label: (ctx) => ` ₵ ${ctx.parsed.y.toLocaleString()}`
                        }
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        grid: {
                          color: 'rgba(0,0,0,0.04)',
                          drawBorder: false,
                        },
                        ticks: {
                          callback: (value) => value >= 1000 ? `${value / 1000}k` : value,
                          font: { size: 11 }
                        }
                      },
                      x: {
                        grid: { display: false },
                        ticks: { font: { size: 11, weight: 'bold' } }
                      }
                    }
                  }}
                />
              </Box>
            </Paper>
          </Box>
        )}

        <CalendarEventSidebar isDesktop={isDesktop} />

      </Box>
    </Box>
  );
};

export default HomePage;
